'use client';

/**
 * `useTurnkeySessionSteward` — platform-neutral core of the Turnkey session
 * steward. The web and RN `TurnkeySessionSteward` components bind their
 * vendor kit's `useTurnkey()` surface into `TurnkeyKitBindings` and mount
 * this hook; everything else (guards, decision, throttle stamp, telemetry,
 * logout fan-out) is shared here.
 *
 * See `session-maintenance.ts` for the WHY and the pure decision core. This
 * hook owns the IO around it:
 *   - triggers: kit-ready, app foreground (`visible`/`resume`/
 *     `bfcache-restore`), connectivity restored
 *   - session reads via the kit's `getSession()` (storage-backed — never the
 *     per-tab `session` React state, which goes stale across tabs)
 *   - the proactive `refreshSession()` call, stamped through
 *     `adapter.storage` so concurrent tabs don't stampede (each refresh
 *     mints a new keypair and clobbers the stored session)
 *   - the reconcile logout: settle delay → re-run every guard against a
 *     fresh `getSession()` → `signalSessionExpired()`
 *
 * Failure posture: a failed `refreshSession()` is NOT retried — the kit
 * internally logs itself out on refresh failure, and the reconcile path is
 * the designed cleanup. Telemetry flows through `onAuthEvent` (the host maps
 * names to its typed event system / Better Stack).
 */
import { useCallback, useEffect, useRef } from 'react';
import { useSdkRuntime } from '../../provider/SdkRuntimeContext.js';
import { useAuthSync } from '../oauth/auth-sync.js';
import { SESSION_TTL_MS } from '../session-policy.js';
import { getSdkAuthStore } from '../state/auth-state.js';
import { decideTurnkeySessionAction, isTurnkeySessionValid, RECONCILE_SETTLE_MS } from './session-maintenance.js';
import { useTurnkeyModalActive } from './turnkey-modal-context.js';
/** Throttle-stamp key in `adapter.storage.sync`. */
export const TURNKEY_LAST_REFRESH_STORAGE_KEY = 'morfi:tk:lastRefreshAt';
/** Policy TTL, in the string-seconds form Turnkey expects, when the kit
 *  config carries none. Policy first, not the session's own TTL, so a
 *  session with a shorter TTL returns to policy on its next refresh. */
const FALLBACK_SESSION_EXPIRATION_SECONDS = String(SESSION_TTL_MS / 1000);
function readLastRefreshAtMs(getItem) {
  try {
    const raw = getItem(TURNKEY_LAST_REFRESH_STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // SILENT-CATCH: storage may be blocked — treat as never-refreshed.
    return null;
  }
}
function sleep(ms) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}
/**
 * Mounts the steward's trigger listeners and check loop. Returns nothing —
 * the platform component renders `null` around it.
 */
export function useTurnkeySessionSteward(options) {
  const {
    adapter
  } = useSdkRuntime();
  const {
    peekFreshLogin,
    isCompletingOauth,
    signalSessionExpired
  } = useAuthSync();
  const modalActive = useTurnkeyModalActive();
  // Latest-value mirror for the async check path — the kit bindings and the
  // modal flag change across renders, but the trigger listeners are
  // long-lived. Synced in an effect (never written during render).
  const latestRef = useRef({
    kit: options.kit,
    modalActive,
    onAuthEvent: options.onAuthEvent
  });
  useEffect(() => {
    latestRef.current = {
      kit: options.kit,
      modalActive,
      onAuthEvent: options.onAuthEvent
    };
  });
  const inFlightRef = useRef(false);
  // One reconcile logout per detection — reset when a live session is seen
  // again (i.e. the user logged back in).
  const reconcileFiredRef = useRef(false);
  const buildInput = useCallback((session, nowMs) => {
    const latest = latestRef.current;
    const auth = getSdkAuthStore().current();
    return {
      session: session ?? null,
      nowMs,
      online: adapter.netInfo.current().online,
      clientReady: latest.kit.clientReady,
      authedWithSubOrg: auth.status === 'authed' && auth.user?.turnkeySubOrgId != null,
      freshLoginPending: peekFreshLogin() !== null,
      completingOauth: isCompletingOauth(),
      modalActive: latest.modalActive,
      lastRefreshAtMs: readLastRefreshAtMs(key => adapter.storage.sync.getItem(key))
    };
  }, [adapter, peekFreshLogin, isCompletingOauth]);
  const runCheck = useCallback(async trigger => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const latest_0 = latestRef.current;
      const session_0 = await latest_0.kit.getSession();
      const nowMs_0 = Date.now();
      if (isTurnkeySessionValid(session_0 ?? null, nowMs_0)) {
        reconcileFiredRef.current = false;
      }
      const action = decideTurnkeySessionAction(buildInput(session_0, nowMs_0));
      if (action === 'refresh') {
        // Stamp BEFORE the call — a concurrent tab's check sees the stamp
        // and skips, so only one tab mints the replacement session.
        try {
          adapter.storage.sync.setItem(TURNKEY_LAST_REFRESH_STORAGE_KEY, String(nowMs_0));
        } catch {
          // SILENT-CATCH: storage may be blocked — refresh anyway.
        }
        try {
          await latest_0.kit.refreshSession({
            // A blank kit value would reach core as no TTL and revive its
            // 15-minute default, so only a non-empty string counts.
            expirationSeconds: latest_0.kit.sessionExpirationSeconds?.trim() || FALLBACK_SESSION_EXPIRATION_SECONDS
          });
          latest_0.onAuthEvent?.('turnkey_session_refreshed', {
            trigger
          });
        } catch {
          // No retry: the kit logs itself out internally on refresh
          // failure; the next check's reconcile path is the cleanup.
          latest_0.onAuthEvent?.('turnkey_refresh_failed', {
            reason: 'provider-refresh-failed',
            trigger
          });
        }
        return;
      }
      if (action === 'reconcile-logout' && !reconcileFiredRef.current) {
        // Settle, then re-run EVERY guard against a fresh storage read —
        // an in-flight login/refresh (this tab or another) clears here.
        await sleep(RECONCILE_SETTLE_MS);
        const recheckSession = await latestRef.current.kit.getSession();
        const recheck = decideTurnkeySessionAction(buildInput(recheckSession, Date.now()));
        if (recheck !== 'reconcile-logout') return;
        reconcileFiredRef.current = true;
        latestRef.current.onAuthEvent?.('turnkey_session_reconcile_logout', {
          trigger
        });
        signalSessionExpired();
      }
    } catch {
      // SILENT-CATCH: steward checks are best-effort background work — a
      // failed storage read must never crash the app; the next trigger
      // re-runs the check.
    } finally {
      inFlightRef.current = false;
    }
  }, [adapter, buildInput, signalSessionExpired]);
  const clientReady = options.kit.clientReady;
  const foregroundDelayMs = options.foregroundDelayMs ?? 0;
  useEffect(() => {
    if (!clientReady) return;
    void runCheck('ready');
    const removeAppListener = adapter.appState.addListener(event => {
      if (event.kind !== 'visible' && event.kind !== 'resume' && event.kind !== 'bfcache-restore') {
        return;
      }
      if (foregroundDelayMs > 0) {
        setTimeout(() => {
          void runCheck('foreground');
        }, foregroundDelayMs);
      } else {
        void runCheck('foreground');
      }
    });
    const removeNetListener = adapter.netInfo.addListener(online => {
      if (online) void runCheck('online');
    });
    return () => {
      removeAppListener();
      removeNetListener();
    };
  }, [clientReady, foregroundDelayMs, adapter, runCheck]);
}